import { useState } from 'react';
import { Search, User } from 'lucide-react';
import { useUsers } from '@/lib/hooks/useUsers';
import { useDebouncedValue } from '@/lib/hooks/useDebouncedValue';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import type { ApiUser } from '@/lib/api/types';

interface UsersBrowserProps {
  onSelect?: (user: ApiUser) => void;
  /** When provided in single mode, fires immediately on click instead of requiring confirmation */
  onSelectImmediate?: (user: ApiUser) => void;
  selectedUserIds?: number[];
  mode?: 'single' | 'multi';
  showSelectedCount?: boolean;
  className?: string;
}

export function UsersBrowser({
  onSelect,
  onSelectImmediate,
  selectedUserIds = [],
  mode = 'single',
  showSelectedCount = true,
  className,
}: UsersBrowserProps) {
  const [searchTerm, setSearchTerm] = useState('');

  // Debounce search to avoid excessive API calls
  const debouncedSearch = useDebouncedValue(searchTerm, 300);

  const { users, isLoading, error } = useUsers({
    name: debouncedSearch || undefined,
    limit: 50,
  });

  const isUserSelected = (userId: number) => {
    return selectedUserIds.includes(userId);
  };

  const handleUserClick = (user: ApiUser) => {
    if (onSelectImmediate && mode === 'single') {
      onSelectImmediate(user);
      return;
    }
    if (onSelect) {
      onSelect(user);
    }
  };

  return (
    <div className={cn('flex flex-col h-full min-w-0', className)}>
      {/* Search Input */}
      <div className="p-4 border-b">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Type a name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-9"
            autoFocus
          />
        </div>
      </div>

      {/* Users List */}
      <div className="flex-1 overflow-y-auto">
        {/* Loading State */}
        {isLoading && (
          <div className="flex items-center justify-center py-8">
            <div className="text-sm text-muted-foreground">Searching...</div>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="flex items-center justify-center py-8">
            <div className="text-sm text-destructive">Failed to load users</div>
          </div>
        )}

        {/* Empty State */}
        {!isLoading && !error && users.length === 0 && (
          <div className="flex items-center justify-center py-8">
            <div className="text-sm text-muted-foreground">
              {searchTerm ? 'No users found' : 'Start typing to search users'}
            </div>
          </div>
        )}

        {/* Users List */}
        {!isLoading && !error && users.length > 0 && (
          <div className="divide-y">
            {users.map((user) => {
              const selected = isUserSelected(user.id);
              return (
                <button
                  key={user.id}
                  onClick={() => handleUserClick(user)}
                  className={cn(
                    'w-full flex items-center gap-3 p-4 hover:bg-muted/50 transition-colors cursor-pointer text-left min-w-0',
                    selected && 'bg-primary/10'
                  )}
                >
                  {/* Avatar */}
                  <div className="flex-shrink-0">
                    {user.image ? (
                      <img
                        src={user.image}
                        alt={user.name || user.email}
                        className="h-10 w-10 rounded-full object-cover"
                      />
                    ) : (
                      <div className="h-10 w-10 rounded-full bg-muted flex items-center justify-center">
                        <User className="h-5 w-5 text-muted-foreground" />
                      </div>
                    )}
                  </div>

                  {/* User Info */}
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm truncate">
                      {user.name || 'Unnamed User'}
                    </div>
                    <div className="text-xs text-muted-foreground truncate">{user.email}</div>
                  </div>

                  {/* Selection Indicator */}
                  {selected && mode === 'multi' && (
                    <div className="flex-shrink-0">
                      <div className="h-5 w-5 rounded-full bg-primary flex items-center justify-center">
                        <svg
                          className="h-3 w-3 text-primary-foreground"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                      </div>
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Selected Count */}
      {showSelectedCount && selectedUserIds.length > 0 && mode === 'multi' && (
        <div className="p-4 border-t bg-muted/30">
          <div className="text-sm text-muted-foreground">
            {selectedUserIds.length} user{selectedUserIds.length !== 1 ? 's' : ''} selected
          </div>
        </div>
      )}
    </div>
  );
}
